(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const escapeHtml = (value = '') => String(value).replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
  })[character]);

  const formatMoney = (cents, currency = 'USD') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const focusable = (element) => $$('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', element)
    .filter((node) => !node.hidden && node.offsetParent !== null);

  class SpringAxis {
    constructor(element, axis = 'x', from = 1, onUpdate = null) {
      this.element = element;
      this.axis = axis;
      this.position = from;
      this.velocity = 0;
      this.target = from;
      this.onUpdate = onUpdate;
      this.raf = 0;
      this.last = performance.now();
      this.render();
    }

    setPosition(position, velocity = 0) {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0;
      this.position = position;
      this.velocity = velocity;
      this.render();
    }

    setTarget(target) {
      this.target = target;
      if (reducedMotion.matches) {
        this.position = target;
        this.velocity = 0;
        this.render();
        this.finish?.(target);
        return;
      }
      if (!this.raf) {
        this.last = performance.now();
        this.raf = requestAnimationFrame((time) => this.step(time));
      }
    }

    step(time) {
      const dt = Math.min((time - this.last) / 1000, .032);
      this.last = time;
      const stiffness = 360;
      const damping = 38;
      const acceleration = (-stiffness * (this.position - this.target)) - (damping * this.velocity);
      this.velocity += acceleration * dt;
      this.position += this.velocity * dt;
      this.render();
      if (Math.abs(this.velocity) < .001 && Math.abs(this.position - this.target) < .001) {
        this.position = this.target;
        this.velocity = 0;
        this.render();
        this.raf = 0;
        this.finish?.(this.target);
        return;
      }
      this.raf = requestAnimationFrame((next) => this.step(next));
    }

    render() {
      const distance = this.position * 100;
      this.element.style.transform = this.axis === 'x'
        ? `translate3d(${distance}%,0,0)`
        : `translate3d(0,${distance}%,0)`;
      this.onUpdate?.(this.position);
    }
  }

  class Drawer {
    constructor({ panel, scrim, openers, closers, direction = 1 }) {
      this.panel = panel;
      this.scrim = scrim;
      this.openers = openers;
      this.closers = closers;
      this.direction = direction;
      window.addEventListener('flotread:drawer-open', event => { if(event.detail !== this.panel.id && this.openState) this.close(); });
      this.lastFocus = null;
      this.openState = false;
      this.drag = null;
      this.spring = new SpringAxis(panel, 'x', direction, (position) => {
        if (!this.scrim.hidden) this.scrim.style.opacity = String(clamp(1 - Math.abs(position), 0, 1));
      });
      this.spring.finish = (target) => {
        if (target !== 0) {
          this.panel.setAttribute('aria-hidden', 'true');
          this.panel.inert = true;
          this.scrim.hidden = true;
          this.scrim.style.opacity = '0';
          if (!document.querySelector('.mobile-menu[aria-hidden="false"],.search-drawer[aria-hidden="false"],.cart-drawer[aria-hidden="false"]')) {
            document.body.classList.remove('drawer-open');
          }
          this.lastFocus?.focus?.();
        }
      };
      this.bind();
    }

    bind() {
      this.openers.forEach((button) => button.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(button);
      }));
      this.closers.forEach((button) => button.addEventListener('click', () => this.close()));
      this.scrim.addEventListener('click', () => this.close());
      this.panel.addEventListener('keydown', (event) => this.onKeydown(event));
      this.panel.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.panel.addEventListener('pointermove', (event) => this.onPointerMove(event));
      this.panel.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.panel.addEventListener('pointercancel', (event) => this.onPointerUp(event));
    }

    open(trigger) {
      this.lastFocus = trigger || document.activeElement;
      this.openState = true;
      this.scrim.hidden = false;
      window.dispatchEvent(new CustomEvent('flotread:drawer-open', {detail: this.panel.id}));
      this.panel.inert = false;
      this.panel.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      this.openers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      requestAnimationFrame(() => {
        this.scrim.animate(
          [{ opacity: Number(getComputedStyle(this.scrim).opacity) || 0 }, { opacity: 1 }],
          { duration: reducedMotion.matches ? 1 : 240, fill: 'forwards', easing: 'ease-out' }
        );
        this.spring.setTarget(0);
        window.setTimeout(() => focusable(this.panel)[0]?.focus(), reducedMotion.matches ? 0 : 120);
      });
    }

    close() {
      this.openState = false;
      this.openers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.spring.setTarget(this.direction);
    }

    onPointerDown(event) {
      if (!this.openState || reducedMotion.matches || event.pointerType === 'mouse' || event.button !== 0) return;
      if (event.target.closest('button,a,input,select,textarea,summary')) return;
      this.panel.setPointerCapture(event.pointerId);
      this.drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startPosition: this.spring.position,
        history: [{ x: event.clientX, time: performance.now() }],
        engaged: false
      };
    }

    onPointerMove(event) {
      if (!this.drag || this.drag.pointerId !== event.pointerId) return;
      const delta = event.clientX - this.drag.startX;
      if (!this.drag.engaged && Math.abs(delta) < 10) return;
      this.drag.engaged = true;
      event.preventDefault();
      const width = Math.max(this.panel.getBoundingClientRect().width, 1);
      let next = this.drag.startPosition + (delta / width);
      const closingProgress = next * this.direction;
      if (closingProgress < 0) {
        const overshoot = closingProgress * width;
        next = this.direction * ((overshoot * width * .36) / (width + .36 * Math.abs(overshoot))) / width;
      } else {
        next = this.direction * Math.min(closingProgress, 1);
      }
      const now = performance.now();
      this.drag.history.push({ x: event.clientX, time: now });
      this.drag.history = this.drag.history.filter((sample) => now - sample.time <= 100);
      this.spring.setPosition(next);
    }

    onPointerUp(event) {
      if (!this.drag || this.drag.pointerId !== event.pointerId) return;
      const drag = this.drag;
      this.drag = null;
      if (this.panel.hasPointerCapture(event.pointerId)) this.panel.releasePointerCapture(event.pointerId);
      if (!drag.engaged) return;
      const samples = drag.history;
      const first = samples[0];
      const last = samples[samples.length - 1];
      const width = Math.max(this.panel.getBoundingClientRect().width, 1);
      const velocity = last && first && last.time > first.time
        ? ((last.x - first.x) / (last.time - first.time)) * 1000 / width
        : 0;
      const projected = this.spring.position + velocity * .18;
      const shouldClose = projected * this.direction > .42 || velocity * this.direction > .48;
      this.spring.velocity = velocity;
      if (shouldClose) this.close();
      else this.spring.setTarget(0);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const nodes = focusable(this.panel);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const menuPanel = $('#MobileMenu');
  const menuScrim = $('[data-menu-close].drawer-scrim');
  const searchPanel = $('#SearchDrawer');
  const searchScrim = $('[data-search-close].drawer-scrim');
  const cartPanel = $('#CartDrawer');
  const cartScrim = $('[data-cart-close].drawer-scrim');

  const menuDrawer = menuPanel && menuScrim ? new Drawer({
    panel: menuPanel,
    scrim: menuScrim,
    openers: $$('[data-menu-open]'),
    closers: $$('[data-menu-close]', menuPanel),
    direction: -1
  }) : null;

  const searchDrawer = searchPanel && searchScrim ? new Drawer({
    panel: searchPanel,
    scrim: searchScrim,
    openers: $$('[data-search-open]'),
    closers: $$('[data-search-close]', searchPanel),
    direction: 1
  }) : null;

  const cartDrawer = cartPanel && cartScrim ? new Drawer({
    panel: cartPanel,
    scrim: cartScrim,
    openers: $$('[data-cart-open]'),
    closers: $$('[data-cart-close]', cartPanel),
    direction: 1
  }) : null;

  // FloTread Apple floating navigation v1.1.6
  const siteHeader = $('[data-site-header]');
  const headerSpring = siteHeader ? new SpringAxis(siteHeader, 'y', 0) : null;
  let lastHeaderScrollY = Math.max(window.scrollY, 0);
  let headerTravel = 0;
  let headerTicking = false;

  const showSiteHeader = () => {
    if (!siteHeader || !headerSpring) return;
    siteHeader.classList.remove('is-hidden');
    headerSpring.setTarget(0);
  };

  const hideSiteHeader = () => {
    if (!siteHeader || !headerSpring || siteHeader.matches(':focus-within')) return;
    siteHeader.classList.add('is-hidden');
    headerSpring.setTarget(-1.22);
  };

  const updateHeader = () => {
    headerTicking = false;
    if (!siteHeader || !headerSpring) return;
    const currentScrollY = Math.max(window.scrollY, 0);
    const delta = currentScrollY - lastHeaderScrollY;
    lastHeaderScrollY = currentScrollY;
    siteHeader.classList.toggle('is-scrolled', currentScrollY > 8);

    if (currentScrollY < 72 || document.body.classList.contains('drawer-open') || siteHeader.matches(':focus-within')) {
      headerTravel = 0;
      showSiteHeader();
      return;
    }
    if (Math.abs(delta) < 1.5) return;

    if (delta > 0) {
      headerTravel = Math.max(0, headerTravel) + delta;
      if (currentScrollY > 120 && headerTravel > 18) {
        headerTravel = 0;
        hideSiteHeader();
      }
    } else {
      headerTravel = Math.min(0, headerTravel) + delta;
      if (headerTravel < -2) {
        headerTravel = 0;
        showSiteHeader();
      }
    }
  };

  const requestHeaderUpdate = () => {
    if (headerTicking) return;
    headerTicking = true;
    requestAnimationFrame(updateHeader);
  };

  siteHeader?.addEventListener('focusin', showSiteHeader);
  updateHeader();
  window.addEventListener('scroll', requestHeaderUpdate, { passive: true });
  window.addEventListener('pageshow', requestHeaderUpdate);

  $$('.button,.icon-button,.category-card,.product-card-action').forEach((element) => {
    const press = () => element.classList.add('is-pressed');
    const release = () => element.classList.remove('is-pressed');
    element.addEventListener('pointerdown', press);
    element.addEventListener('pointerup', release);
    element.addEventListener('pointercancel', release);
    element.addEventListener('pointerleave', release);
  });

  const revealNodes = $$('[data-reveal]');
  if (reducedMotion.matches || !('IntersectionObserver' in window)) {
    revealNodes.forEach((element) => element.classList.add('is-visible'));
  } else {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: .08 });
    revealNodes.forEach((element, index) => {
      element.style.transitionDelay = `${Math.min((index % 4) * 55, 165)}ms`;
      observer.observe(element);
    });
  }

  const heroImage = $('[data-parallax-hero] .performance-hero-image');
  if (heroImage && !reducedMotion.matches) {
    let ticking = false;
    const updateHero = () => {
      const rect = heroImage.parentElement.getBoundingClientRect();
      const progress = clamp(-rect.top / Math.max(rect.height, 1), 0, 1);
      heroImage.style.transform = `translate3d(0,${progress * 2.4}%,0) scale(1.02)`;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(updateHero);
      }
    }, { passive: true });
  }

  const predictiveInput = $('[data-predictive-search]');
  const predictiveResults = $('[data-predictive-results]');
  let searchTimer = 0;
  let searchAbort;
  predictiveInput?.addEventListener('input', () => {
    clearTimeout(searchTimer); searchAbort?.abort();
    const query = predictiveInput.value.trim();
    if (query.length < 2) {
      predictiveResults.innerHTML = '<p>Start typing to find products.</p>';
      return;
    }
    predictiveResults.innerHTML = '<p>Searching…</p>';
    searchTimer = window.setTimeout(async () => {
      try {
        const url = `${window.flotreadRoutes.search}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`;
        searchAbort=new AbortController();
        const response = await fetch(url, { signal:searchAbort.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Search unavailable');
        const data = await response.json();
        if(predictiveInput.value.trim()!==query)return;
        const products = data.resources?.results?.products || [];
        predictiveResults.innerHTML = products.length ? products.map((product) => `
          <a class="predictive-item" href="${escapeHtml(product.url)}">
            ${product.image ? `<img src="${escapeHtml(product.image)}${product.image.includes('?') ? '&' : '?'}width=180" alt="">` : '<span></span>'}
            <strong>${escapeHtml(product.title)}</strong>
            <span>${escapeHtml(product.price)}</span>
          </a>
        `).join('') : '<p>No products found.</p>';
      } catch (error) {
        if(error.name==='AbortError')return;
        predictiveResults.innerHTML = '<p>Suggestions are unavailable. Press Enter to search all products.</p>';
      }
    }, 180);
  });

  const cartCountNodes = $$('[data-cart-count]');
  const cartBody = $('[data-cart-drawer-body]');
  const cartFooter = $('[data-cart-drawer-footer]');
  const cartTotal = $('[data-cart-total]');
  const shippingMessage = $('[data-shipping-message]');
  const shippingBar = $('[data-shipping-bar]');
  const cartEmptyTemplate = $('[data-cart-empty-template]');

  const updateShippingProgress = (cart) => {
    const threshold = Number(window.flotreadRoutes.freeShippingThreshold || 0);
    if (!threshold) return;
    const progress = clamp((cart.total_price / threshold) * 100, 0, 100);
    if (shippingBar) shippingBar.style.width = `${progress}%`;
    if (shippingMessage) {
      shippingMessage.textContent = cart.total_price >= threshold
        ? 'You unlocked free shipping.'
        : `${formatMoney(threshold - cart.total_price, cart.currency)} away from free shipping.`;
    }
  };

  const cartOptionLabel = (name) => /size/i.test(name) ? 'Size' : /^colou?r$/i.test(name) ? 'Color' : name;
  const cartOptionsMarkup = (item) => {
    const options = (item.options_with_values || []).filter(option => option.value && !(option.name === 'Title' && option.value === 'Default Title'));
    if (options.length) return '<p class="drawer-cart-options">' + options.map(option => '<span class="drawer-cart-option"><span class="drawer-cart-option__label">' + escapeHtml(cartOptionLabel(option.name)) + ':</span> <span>' + escapeHtml(option.value) + '</span></span>').join('') + '</p>';
    return item.variant_title && item.variant_title !== 'Default Title' ? '<p>' + escapeHtml(item.variant_title) + '</p>' : '';
  };

  const renderCart = (cart) => {
    cartCountNodes.forEach((node) => { node.textContent = cart.item_count; });
    updateShippingProgress(cart);
    if (!cart.items.length) {
      if (cartBody) cartBody.innerHTML = cartEmptyTemplate?.innerHTML || '';
      if (cartFooter) cartFooter.hidden = true;
      return;
    }
    if (cartBody) {
      cartBody.innerHTML = cart.items.map((item, index) => `
        <article class="drawer-cart-item" data-cart-line="${index + 1}" data-cart-key="${escapeHtml(item.key)}">
          <a href="${escapeHtml(item.url)}" class="drawer-cart-image">${item.image ? `<img src="${escapeHtml(item.image)}&width=300" alt="${escapeHtml(item.product_title)}">` : ''}</a>
          <div class="drawer-cart-copy">
            <div class="drawer-cart-heading"><a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a><strong>${formatMoney(item.final_line_price, cart.currency)}</strong></div>
            ${cartOptionsMarkup(item)}
            <div class="drawer-cart-actions">
              <div class="drawer-quantity"><button type="button" data-cart-quantity="-1" aria-label="Decrease quantity">−</button><span>${item.quantity}</span><button type="button" data-cart-quantity="1" aria-label="Increase quantity">+</button></div>
              <button type="button" class="drawer-remove" data-cart-remove>Remove</button>
            </div>
          </div>
        </article>
      `).join('');
    }
    if (cartTotal) cartTotal.textContent = formatMoney(cart.total_price, cart.currency);
    if (cartFooter) cartFooter.hidden = false;
  };

  const getCart = async () => {
    const response = await fetch(`${window.flotreadRoutes.cart}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart unavailable');
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };

  $$('[data-cart-open]').forEach((button) => {
    button.addEventListener('click', async () => {
      try { await getCart(); } catch (error) { /* keep server-rendered state */ }
    });
  });

  cartBody?.addEventListener('click', async (event) => {
    const item = event.target.closest('[data-cart-line]');
    if (!item) return;
    const delta = event.target.closest('[data-cart-quantity]');
    const remove = event.target.closest('[data-cart-remove]');
    if (!delta && !remove) return;
    const current = Number($('.drawer-quantity span', item)?.textContent || 1);
    const quantity = remove ? 0 : Math.max(0, current + Number(delta.dataset.cartQuantity));
    if(item.dataset.busy) return;
    item.dataset.busy = 'true';
    item.querySelectorAll('button').forEach(button => button.disabled = true);
    const errorNode = $('[data-cart-error]'); if(errorNode) errorNode.textContent='';
    item.style.opacity = '.45';
    try {
      const response = await fetch(`${window.flotreadRoutes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.dataset.cartKey, quantity })
      });
      if (!response.ok) throw new Error('Unable to update cart');
      renderCart(await response.json());
    } catch (error) {
      item.style.opacity = ''; delete item.dataset.busy;
      item.querySelectorAll('button').forEach(button => button.disabled = false);
      if(errorNode) errorNode.textContent='We could not update your bag. Please try again.';
    }
  });

  $$('[data-sort-select]').forEach((select) => {
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value); url.searchParams.delete('page');
      window.location.assign(url.toString());
    });
  });

  // FloTread mobile native swipe gallery v1 — browser-owned momentum and interruption
  $$('[data-product-gallery]').forEach((gallery) => {
    const slides = $$('[data-product-slide]', gallery);
    const thumbnails = $$('[data-gallery-thumbnail]', gallery);
    const current = $('[data-gallery-current]', gallery);
    const stage = $('.shoe-gallery__stage, .product-gallery-stage', gallery);
    if (!slides.length || !stage) return;
    const mobile = window.matchMedia('(max-width: 749px)');
    const track = document.createElement('div');
    track.className = 'svr-gallery-track';
    track.tabIndex = 0;
    track.setAttribute('role', 'region');
    track.setAttribute('aria-label', 'Product images. Swipe left or right to browse.');
    stage.insertBefore(track, slides[0]);
    slides.forEach(slide => track.appendChild(slide));
    gallery.dataset.swipeReady = 'true';
    let index = Math.max(0, slides.findIndex(slide => slide.classList.contains('is-active')));
    let frame = 0;
    let pendingTarget = null;
    let settleTimer = 0;
    const update = (next) => {
      index = next;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
        if (!active) slide.querySelectorAll('video').forEach(video => video.pause());
      });
      thumbnails.forEach((thumb, thumbIndex) => {
        thumb.classList.toggle('is-active', thumbIndex === index);
        thumb.setAttribute('aria-current', thumbIndex === index ? 'true' : 'false');
      });
      if (current) current.textContent = index + 1;
      const thumb = thumbnails[index];
      const strip = thumb?.parentElement;
      if (thumb && strip && strip.scrollWidth > strip.clientWidth) {
        const left = thumb.offsetLeft - strip.offsetLeft;
        if (left < strip.scrollLeft || left + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) {
          strip.scrollTo({ left: left - (strip.clientWidth - thumb.offsetWidth) / 2, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
        }
      }
    };
    const show = (next, instant = false) => {
      const target = mobile.matches ? clamp(next, 0, slides.length - 1) : (next + slides.length) % slides.length;
      pendingTarget = mobile.matches ? target : null;
      update(target);
      if (mobile.matches) track.scrollTo({left: target * track.clientWidth, behavior: instant || reducedMotion.matches ? 'instant' : 'smooth'});
    };
    const syncPosition = () => {
      const next = clamp(Math.round(track.scrollLeft / Math.max(1, track.clientWidth)), 0, slides.length - 1);
      if (next !== index) update(next);
    };
    const settle = () => {
      clearTimeout(settleTimer);
      pendingTarget = null;
      syncPosition();
    };
    track.addEventListener('scroll', () => {
      if (!mobile.matches) return;
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 180);
      if (frame || pendingTarget !== null) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (pendingTarget === null) syncPosition();
      });
    }, {passive: true});
    track.addEventListener('scrollend', settle);
    const interrupt = () => {
      if (pendingTarget !== null) track.scrollTo({left: track.scrollLeft, behavior: 'instant'});
      pendingTarget = null;
      clearTimeout(settleTimer);
    };
    track.addEventListener('pointerdown', interrupt, {passive: true});
    track.addEventListener('touchstart', interrupt, {passive: true});
    track.addEventListener('wheel', interrupt, {passive: true});
    track.addEventListener('keydown', event => {
      if (event.target !== track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      show(event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : index + (event.key === 'ArrowRight' ? 1 : -1));
    });
    $('[data-gallery-prev]', gallery)?.addEventListener('click', () => show(index - 1));
    $('[data-gallery-next]', gallery)?.addEventListener('click', () => show(index + 1));
    thumbnails.forEach((thumb, thumbIndex) => thumb.addEventListener('click', event => { event.preventDefault(); show(thumbIndex); }));
    gallery.addEventListener('flotread:show-media', event => {
      const nextIndex = slides.findIndex(slide => String(slide.dataset.mediaId || '') === String(event.detail?.mediaId || ''));
      if (nextIndex >= 0) show(nextIndex);
    });
    mobile.addEventListener('change', () => show(index, true));
    let lastWidth = 0;
    new ResizeObserver(() => {
      const width = track.clientWidth;
      if (mobile.matches && width !== lastWidth) { lastWidth = width; show(index, true); }
    }).observe(stage);
    show(index, true);
  });

  // Full bag quantity controls
  $$('[data-cart-page-quantity]').forEach((button) => {
    button.addEventListener('click', () => {
      const wrapper = button.closest('.cart-page-quantity');
      const input = wrapper ? $('[data-cart-page-quantity-input]', wrapper) : null;
      if (!input) return;
      input.value = Math.max(0, Number(input.value || 0) + Number(button.dataset.cartPageQuantity || 0));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  $$('[data-product-form]').forEach((form) => {
    const variantsScript = $('[data-product-variants]', form.closest('[data-product-page]'));
    const variants = window.flotreadFullVariants?.[form.closest('[data-product-page]').dataset.productHandle] || (variantsScript ? JSON.parse(variantsScript.textContent) : []);
    const optionInputs = $$('[data-variant-option]', form);
    const fullMatrix = Boolean(window.flotreadFullVariants?.[form.closest('[data-product-page]').dataset.productHandle]);
    let variantValidation; let validationSequence=0;
    let activeVariantMediaId = null;
    const variantSelect = $('[data-variant-id]', form);
    const addButton = $('[data-add-to-cart]', form);
    const addText = $('[data-add-to-cart-text]', form);
    const price = $('[data-product-price]', form.closest('[data-product-page]'));
    const compare = $('[data-compare-price]', form.closest('[data-product-page]'));
    // FloTread sale price presentation v1.1.8
    const priceSave = $('[data-price-save]', form.closest('[data-product-page]'));

    // FloTread media-change-only gallery return v1.1.28
    const updateVariant = ({ returnToGallery = false, serverValidated = false } = {}) => {
      const selected = $$('[data-option-group]', form).map((group) => $('[data-variant-option]:checked', group)?.value);
      const variant = variants.find((candidate) => candidate.options.every((value, optionIndex) => value === selected[optionIndex])) || (optionInputs.length ? null : variants[0]);
      const status = $('[data-product-status]', form.closest('[data-product-page]'));
      const sku = $('[data-variant-sku]', form.closest('[data-product-page]'));
      $$('[data-option-group]', form).forEach((group, optionIndex) => {
        $$('[data-variant-option]', group).forEach(input => {
          const compatible = variants.some(candidate => (fullMatrix || candidate.available) && candidate.options[optionIndex] === input.value && candidate.options.every((value, i) => i === optionIndex || value === selected[i]));
          input.disabled = !compatible && !input.checked;
          input.setAttribute('aria-disabled', String(!compatible));
          input.nextElementSibling?.classList.toggle('is-unavailable', !compatible);
        });
      });
      if (!variant) { variantSelect.value=''; addButton.disabled=true; addText.textContent='Unavailable'; if(status)status.textContent='This combination is unavailable. Choose another option.'; if(sku)sku.textContent=''; const checkout=$('.dynamic-checkout',form);if(checkout)checkout.hidden=true;return; }
      if(sku)sku.textContent=variant.sku ? 'SKU: '+variant.sku : '';
      if(status)status.textContent=variant.available ? '' : 'This option is sold out.';
      const checkout=$('.dynamic-checkout',form);if(checkout)checkout.hidden=!variant.available;
      const url=new URL(location.href);url.searchParams.set('variant',variant.id);history.replaceState(null,'',url);

      if(variantSelect.tagName==='SELECT' && !Array.from(variantSelect.options).some(o=>String(o.value)===String(variant.id))) variantSelect.add(new Option(variant.title,variant.id));
      variantSelect.value = variant.id;
      if (price && variant.price != null) price.textContent = formatMoney(variant.price, window.Shopify?.currency?.active || 'USD');
      if (compare) {
        compare.hidden = !(variant.compare_at_price > variant.price);
        compare.textContent = variant.compare_at_price ? formatMoney(variant.compare_at_price, window.Shopify?.currency?.active || 'USD') : '';
      }
      if (priceSave) {
        const isOnSale = variant.compare_at_price > variant.price;
        priceSave.hidden = !isOnSale;
        priceSave.textContent = isOnSale ? `Save ${Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)}%` : '';
      }
      addButton.disabled = !variant.available;
      addText.textContent = variant.available ? 'Add to bag' : 'Sold out';
      const page = form.closest('[data-product-page]');
      const gallery = page ? $('[data-product-gallery]', page) : null;
      const mediaId = variant.featured_media?.id;
      const mediaChanged = Boolean(mediaId && mediaId !== activeVariantMediaId);
      if (gallery && mediaChanged) gallery.dispatchEvent(new CustomEvent('flotread:show-media', { detail: { mediaId } }));
      if (returnToGallery && mediaChanged && gallery && window.matchMedia('(max-width: 820px)').matches) {
        const stage = $('.shoe-gallery__stage', gallery) || gallery;
        requestAnimationFrame(() => requestAnimationFrame(() => {
          const headerOffset = (siteHeader?.getBoundingClientRect().height || 0) + 12;
          const top = Math.max(stage.getBoundingClientRect().top + window.scrollY - headerOffset, 0);
          window.scrollTo({ top, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        }));
      }
      activeVariantMediaId = mediaId || null;
      if(fullMatrix && !serverValidated){
        variantValidation?.abort(); variantValidation=new AbortController(); const sequence=++validationSequence;
        addButton.disabled=true; addText.textContent='Checking…'; if(status)status.textContent='Checking availability…'; if(checkout)checkout.hidden=true;
        const checkUrl=new URL(location.pathname,location.origin);checkUrl.searchParams.set('variant',variant.id);checkUrl.searchParams.set('section_id',page.dataset.productSection);
        fetch(checkUrl,{signal:variantValidation.signal}).then(response=>{if(!response.ok)throw Error('Availability check unavailable');return response.text();}).then(html=>{
          if(sequence!==validationSequence)return;
          const document=new DOMParser().parseFromString(html,'text/html');const selected=document.querySelector('[data-server-selected-variant]');const fresh=selected?JSON.parse(selected.textContent):null;
          if(!fresh || String(fresh.id)!==String(variant.id))throw Error('Variant unavailable');
          Object.assign(variant,fresh); updateVariant({serverValidated:true});
        }).catch(error=>{if(error.name==='AbortError'||sequence!==validationSequence)return;addButton.disabled=true;addText.textContent='Unavailable';if(status)status.textContent='We could not confirm availability. Select this option again or reload the page.';});
      }

      $$('[data-option-group]', form).forEach((group) => {
        const value = $('[data-variant-option]:checked', group)?.value || '';
        const label = $('[data-option-value]', group);
        if (label) label.textContent = value;
      });
    };

    optionInputs.forEach((input) => input.addEventListener('change', () => updateVariant({ returnToGallery: true })));
    const deepId = new URL(location.href).searchParams.get('variant'); const initialVariant=variants.find(v=>String(v.id)===deepId) || variants.find(v=>v.id===Number(variantSelect?.value));
    if(initialVariant) $$('[data-option-group]',form).forEach((group,index)=>$$('[data-variant-option]',group).forEach(input=>{input.checked=input.value===initialVariant.options[index];}));
    updateVariant();

    window.addEventListener('popstate',()=>{const id=new URL(location.href).searchParams.get('variant');const variant=variants.find(v=>String(v.id)===id);if(variant){$$('[data-option-group]',form).forEach((group,index)=>$$('[data-variant-option]',group).forEach(input=>input.checked=input.value===variant.options[index]));updateVariant();}});
    const quantity = $('[data-quantity-input]', form);
    $('[data-quantity-decrease]', form)?.addEventListener('click', () => { quantity.value = Math.max(1, Number(quantity.value || 1) - 1); });
    $('[data-quantity-increase]', form)?.addEventListener('click', () => { quantity.value = Number(quantity.value || 1) + 1; });

    form.addEventListener('submit', async (event) => {
      if (!cartDrawer) return;
      event.preventDefault();
      if(addButton.disabled || !variantSelect.value || form.dataset.busy) return;
      form.dataset.busy='true';
      addButton.disabled = true;
      addText.textContent = 'Adding…';
      try {
        const response = await fetch(`${window.flotreadRoutes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        if (!response.ok) throw new Error('Unable to add item');
        await getCart();
        cartDrawer.open(addButton);
        addText.textContent = 'Added';
        window.setTimeout(() => {
          delete form.dataset.busy; updateVariant();
        }, 900);
      } catch (error) {
        delete form.dataset.busy; updateVariant();
        const status=$('[data-product-status]',form.closest('[data-product-page]'));if(status)status.textContent='Unable to add this item. Please try again.';
      }
    });
  });
})();

